import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { 
  FileText,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Copy,
  ListChecks,
  BookTemplate,
  Building2,
  Search,
  Tag,
  Upload,
  Download,
  File,
  Image,
  FileSpreadsheet,
  FileType
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fmtTz } from "../lib/time";

export default function AdminDocuments() {
  const { authAxios } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [savedLists, setSavedLists] = useState([]);
  const [projects, setProjects] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editingList, setEditingList] = useState(null);
  const [duplicatingDoc, setDuplicatingDoc] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [docFormData, setDocFormData] = useState({
    title: '',
    content: '',
    project_id: '',
    is_template: false,
    tags: []
  });
  const [listFormData, setListFormData] = useState({
    name: '',
    content: '',
    category: ''
  });
  const [duplicateFormData, setDuplicateFormData] = useState({
    new_title: '',
    project_id: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [docsRes, templatesRes, listsRes, projRes, filesRes] = await Promise.all([
        authAxios.get('/documents?is_template=false'),
        authAxios.get('/templates'),
        authAxios.get('/saved-lists'),
        authAxios.get('/projects'),
        authAxios.get('/files')
      ]);
      setDocuments(docsRes.data);
      setTemplates(templatesRes.data);
      setSavedLists(listsRes.data);
      setProjects(projRes.data);
      setUploadedFiles(filesRes.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // File upload handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await uploadFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = async (e) => {
    if (e.target.files && e.target.files[0]) {
      await uploadFiles(e.target.files);
    }
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await authAxios.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(`${files.length} file(s) uploaded successfully!`);
      await fetchData();
    } catch (error) {
      toast.error('Failed to upload file(s)');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadFile = async (file) => {
    try {
      const res = await authAxios.get(`/files/${file.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      const detail = error.response?.status === 404 ? 'This file is no longer available. Please upload it again.' : 'Failed to download file';
      toast.error(detail);
    }
  };

  const handleDeleteFile = async (file) => {
    if (!window.confirm(`Delete "${file.original_filename}"?`)) return;
    try {
      await authAxios.delete(`/files/${file.id}`);
      toast.success('File deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return Image;
    if (contentType?.includes('spreadsheet') || contentType?.includes('excel')) return FileSpreadsheet;
    if (contentType?.includes('pdf')) return FileType;
    return File;
  };

  // Document handlers
  const resetDocForm = () => {
    setDocFormData({ title: '', content: '', project_id: '', is_template: false, tags: [] });
    setEditingDoc(null);
  };

  const openCreateDoc = (isTemplate = false) => {
    resetDocForm();
    setDocFormData(prev => ({ ...prev, is_template: isTemplate }));
    setDocDialogOpen(true);
  };

  const openEditDoc = (doc) => {
    setEditingDoc(doc);
    setDocFormData({
      title: doc.title,
      content: doc.content,
      project_id: doc.project_id || '',
      is_template: doc.is_template,
      tags: doc.tags || []
    });
    setDocDialogOpen(true);
  };

  const handleDocSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...docFormData, project_id: docFormData.project_id || null };
      if (editingDoc) {
        await authAxios.put(`/documents/${editingDoc.id}`, payload);
        toast.success('Document updated!');
      } else {
        await authAxios.post('/documents', payload);
        toast.success(docFormData.is_template ? 'Template created!' : 'Document created!');
      }
      setDocDialogOpen(false);
      resetDocForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"?`)) return;
    try {
      await authAxios.delete(`/documents/${doc.id}`);
      toast.success('Document deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openDuplicateDialog = (doc) => {
    setDuplicatingDoc(doc);
    setDuplicateFormData({ new_title: `${doc.title} (Copy)`, project_id: '' });
    setDuplicateDialogOpen(true);
  };

  const handleDuplicate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await authAxios.post(`/documents/${duplicatingDoc.id}/duplicate`, {
        new_title: duplicateFormData.new_title,
        project_id: duplicateFormData.project_id || null
      });
      toast.success('Document duplicated!');
      setDuplicateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to duplicate');
    } finally {
      setSubmitting(false);
    }
  };

  // Saved List handlers
  const resetListForm = () => {
    setListFormData({ name: '', content: '', category: '' });
    setEditingList(null);
  };

  const openCreateList = () => {
    resetListForm();
    setListDialogOpen(true);
  };

  const openEditList = (list) => {
    setEditingList(list);
    setListFormData({ name: list.name, content: list.content, category: list.category || '' });
    setListDialogOpen(true);
  };

  const handleListSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingList) {
        await authAxios.put(`/saved-lists/${editingList.id}`, listFormData);
        toast.success('List updated!');
      } else {
        await authAxios.post('/saved-lists', listFormData);
        toast.success('List created!');
      }
      setListDialogOpen(false);
      resetListForm();
      fetchData();
    } catch (error) {
      toast.error('Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteList = async (list) => {
    if (!window.confirm(`Delete "${list.name}"?`)) return;
    try {
      await authAxios.delete(`/saved-lists/${list.id}`);
      toast.success('List deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed');
    }
  };

  const insertList = (list) => {
    setDocFormData(prev => ({
      ...prev,
      content: prev.content + (prev.content ? '\n\n' : '') + `## ${list.name}\n${list.content}`
    }));
    toast.success(`"${list.name}" inserted!`);
  };

  const filteredDocs = documents.filter(d => 
    d.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.project_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-documents">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Documents & Templates</h1>
          <p className="text-stone-500 mt-1">Create documents with reusable lists</p>
        </div>
      </div>

      <Tabs defaultValue="documents" className="space-y-6">
        <TabsList className="bg-stone-100 p-1 rounded-xl">
          <TabsTrigger value="documents" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow">
            <FileText className="w-4 h-4 mr-2" />Documents
          </TabsTrigger>
          <TabsTrigger value="templates" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow">
            <BookTemplate className="w-4 h-4 mr-2" />Templates
          </TabsTrigger>
          <TabsTrigger value="files" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow">
            <Upload className="w-4 h-4 mr-2" />Files
          </TabsTrigger>
          <TabsTrigger value="lists" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow">
            <ListChecks className="w-4 h-4 mr-2" />Saved Lists
          </TabsTrigger>
        </TabsList>

        {/* DOCUMENTS TAB */}
        <TabsContent value="documents">
          <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <FileText className="w-5 h-5 text-stone-400" />Documents ({filteredDocs.length})
              </CardTitle>
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 w-48 bg-stone-50" />
                </div>
                <Button onClick={() => openCreateDoc(false)} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="create-doc-btn">
                  <Plus className="w-5 h-5 mr-2" />New Document
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
              ) : filteredDocs.length === 0 ? (
                <div className="text-center py-12 text-stone-500"><FileText className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No documents yet</p></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredDocs.map(doc => (
                    <div key={doc.id} className="border border-stone-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-semibold text-stone-900 truncate flex-1">{doc.title}</h3>
                        <div className="flex gap-1 ml-2">
                          <Button variant="ghost" size="icon" onClick={() => openDuplicateDialog(doc)} className="h-8 w-8 hover:bg-blue-50"><Copy className="w-4 h-4 text-blue-600" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => openEditDoc(doc)} className="h-8 w-8 hover:bg-emerald-50"><Pencil className="w-4 h-4 text-stone-600" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteDoc(doc)} className="h-8 w-8 hover:bg-rose-50"><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                        </div>
                      </div>
                      {doc.project_name && (
                        <div className="flex items-center gap-1 text-sm text-stone-500 mb-2">
                          <Building2 className="w-4 h-4" />{doc.project_name}
                        </div>
                      )}
                      <p className="text-sm text-stone-500 line-clamp-2 mb-3">{doc.content?.substring(0, 100)}...</p>
                      <div className="flex items-center justify-between text-xs text-stone-400">
                        <span>{fmtTz(doc.updated_at, 'MMM d, yyyy')}</span>
                        <span>{doc.created_by_name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TEMPLATES TAB */}
        <TabsContent value="templates">
          <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <BookTemplate className="w-5 h-5 text-stone-400" />Templates ({templates.length})
              </CardTitle>
              <Button onClick={() => openCreateDoc(true)} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="create-template-btn">
                <Plus className="w-5 h-5 mr-2" />New Template
              </Button>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <div className="text-center py-12 text-stone-500"><BookTemplate className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No templates yet. Save a document as template!</p></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.map(doc => (
                    <div key={doc.id} className="border border-purple-200 bg-purple-50/30 rounded-xl p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <BookTemplate className="w-5 h-5 text-purple-600" />
                          <h3 className="font-semibold text-stone-900 truncate">{doc.title}</h3>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openDuplicateDialog(doc)} className="h-8 w-8 hover:bg-purple-100" title="Use this template"><Copy className="w-4 h-4 text-purple-600" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => openEditDoc(doc)} className="h-8 w-8 hover:bg-emerald-50"><Pencil className="w-4 h-4 text-stone-600" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteDoc(doc)} className="h-8 w-8 hover:bg-rose-50"><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                        </div>
                      </div>
                      <p className="text-sm text-stone-500 line-clamp-3 mb-3">{doc.content?.substring(0, 150)}...</p>
                      <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-200">Template</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SAVED LISTS TAB */}
        <TabsContent value="lists">
          <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-stone-400" />Saved Lists ({savedLists.length})
              </CardTitle>
              <Button onClick={openCreateList} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="create-list-btn">
                <Plus className="w-5 h-5 mr-2" />New List
              </Button>
            </CardHeader>
            <CardContent>
              {savedLists.length === 0 ? (
                <div className="text-center py-12 text-stone-500"><ListChecks className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No saved lists yet. Create reusable checklists!</p></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {savedLists.map(list => (
                    <div key={list.id} className="border border-stone-200 rounded-xl p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-stone-900">{list.name}</h3>
                          {list.category && <Badge variant="outline" className="mt-1 text-xs">{list.category}</Badge>}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditList(list)} className="h-8 w-8 hover:bg-emerald-50"><Pencil className="w-4 h-4 text-stone-600" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteList(list)} className="h-8 w-8 hover:bg-rose-50"><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                        </div>
                      </div>
                      <pre className="text-sm text-stone-600 whitespace-pre-wrap bg-stone-50 rounded p-3 max-h-32 overflow-auto">{list.content}</pre>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* FILES TAB */}
        <TabsContent value="files">
          <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Upload className="w-5 h-5 text-stone-400" />
                File Uploads ({uploadedFiles.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Drag and Drop Upload Area */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                  dragActive 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-stone-200 hover:border-emerald-300 hover:bg-stone-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileInput}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  data-testid="file-upload-input"
                />
                <div className="flex flex-col items-center gap-3">
                  {uploading ? (
                    <Loader2 className="w-12 h-12 text-emerald-600 animate-spin" />
                  ) : (
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center ${dragActive ? 'bg-emerald-100' : 'bg-stone-100'}`}>
                      <Upload className={`w-8 h-8 ${dragActive ? 'text-emerald-600' : 'text-stone-400'}`} />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-stone-700">
                      {uploading ? 'Uploading...' : dragActive ? 'Drop files here' : 'Drag & drop files here'}
                    </p>
                    <p className="text-sm text-stone-400 mt-1">
                      or click to browse • PDF, DOCX, XLSX, images, and more
                    </p>
                  </div>
                </div>
              </div>

              {/* Files List */}
              {uploadedFiles.length === 0 ? (
                <div className="text-center py-8 text-stone-400">
                  <File className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No files uploaded yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {uploadedFiles.map(file => {
                    const FileIcon = getFileIcon(file.content_type);
                    return (
                      <div 
                        key={file.id} 
                        className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors"
                        data-testid={`file-item-${file.id}`}
                      >
                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-stone-200">
                          <FileIcon className="w-5 h-5 text-stone-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-stone-900 truncate">{file.original_filename}</p>
                          <p className="text-xs text-stone-400">
                            {formatFileSize(file.file_size)} • Uploaded {fmtTz(file.uploaded_at, 'MMM d, yyyy')} by {file.uploaded_by_name}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleDownloadFile(file)}
                            className="hover:bg-emerald-50 hover:border-emerald-200"
                            data-testid={`download-file-${file.id}`}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleDeleteFile(file)}
                            className="hover:bg-rose-50"
                            data-testid={`delete-file-${file.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-rose-600" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Document/Template Dialog */}
      <Dialog open={docDialogOpen} onOpenChange={setDocDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingDoc ? 'Edit' : 'Create'} {docFormData.is_template ? 'Template' : 'Document'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDocSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="docTitle">Title</Label>
              <Input id="docTitle" value={docFormData.title} onChange={(e) => setDocFormData({ ...docFormData, title: e.target.value })} required className="bg-stone-50 border-stone-200" />
            </div>
            {!docFormData.is_template && (
              <div className="space-y-2">
                <Label>Project (optional)</Label>
                <Select value={docFormData.project_id || "none"} onValueChange={(v) => setDocFormData({ ...docFormData, project_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-stone-50 border-stone-200"><SelectValue placeholder="General (no project)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">General (no project)</SelectItem>
                    {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="docContent">Content</Label>
                {savedLists.length > 0 && (
                  <Select onValueChange={(id) => { const list = savedLists.find(l => l.id === id); if (list) insertList(list); }}>
                    <SelectTrigger className="w-auto h-8 text-xs bg-emerald-50 border-emerald-200 text-emerald-700">
                      <ListChecks className="w-3 h-3 mr-1" />Insert List
                    </SelectTrigger>
                    <SelectContent>
                      {savedLists.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <Textarea id="docContent" value={docFormData.content} onChange={(e) => setDocFormData({ ...docFormData, content: e.target.value })} required className="bg-stone-50 border-stone-200 min-h-[300px] font-mono text-sm" placeholder="Write your document content here..." />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={docFormData.is_template} onCheckedChange={(c) => setDocFormData({ ...docFormData, is_template: c })} />
              <Label>Save as Template</Label>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-500 text-white">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : editingDoc ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Saved List Dialog */}
      <Dialog open={listDialogOpen} onOpenChange={setListDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingList ? 'Edit' : 'Create'} Saved List</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleListSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="listName">List Name</Label>
              <Input id="listName" value={listFormData.name} onChange={(e) => setListFormData({ ...listFormData, name: e.target.value })} required className="bg-stone-50 border-stone-200" placeholder="e.g. Safety Checklist" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="listCategory">Category (optional)</Label>
              <Input id="listCategory" value={listFormData.category} onChange={(e) => setListFormData({ ...listFormData, category: e.target.value })} className="bg-stone-50 border-stone-200" placeholder="e.g. Safety, Tools, Procedures" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="listContent">Content</Label>
              <Textarea id="listContent" value={listFormData.content} onChange={(e) => setListFormData({ ...listFormData, content: e.target.value })} required className="bg-stone-50 border-stone-200 min-h-[200px] font-mono text-sm" placeholder="- Item 1&#10;- Item 2&#10;- Item 3" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-500 text-white w-full">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : editingList ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Duplicate Document</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDuplicate} className="space-y-4">
            <div className="space-y-2">
              <Label>New Title</Label>
              <Input value={duplicateFormData.new_title} onChange={(e) => setDuplicateFormData({ ...duplicateFormData, new_title: e.target.value })} required className="bg-stone-50 border-stone-200" />
            </div>
            <div className="space-y-2">
              <Label>Assign to Project (optional)</Label>
              <Select value={duplicateFormData.project_id || "none"} onValueChange={(v) => setDuplicateFormData({ ...duplicateFormData, project_id: v === "none" ? "" : v })}>
                <SelectTrigger className="bg-stone-50 border-stone-200"><SelectValue placeholder="General (no project)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">General (no project)</SelectItem>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-500 text-white w-full">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Duplicating...</> : 'Duplicate'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
